import React, { createContext, useContext, useState, useEffect } from "react";

/**
 * Ultra-light i18n. Wraps app. `t(key)` returns localized string.
 * Language selector persists in localStorage.
 */

const DICT = {
  en: {
    "cat.name": "Milkoş",
    "cat.intro": "hello, my name is Milkoş 🐾",
    "sec.manifesto": "she reads everything twice.",
    "sec.works": "six internships. i counted.",
    "sec.pour": "brew me a tiny one too?",
    "sec.projects": "these are still cooking.",
    "sec.grid": "match 'em! i'm rooting for you.",
    "sec.skills": "she also knows how to open cans.",
    "sec.contact": "purr-please write her a note ♡",
    "menu.title": "Play with Milkoş",
    "menu.runs": "Milkoş Runs",
    "menu.fish": "Fish Catch",
    "menu.yarn": "Yarn Chase",
    "menu.nap": "Nap Timer",
    "menu.mice": "Milkoş’s Mice",
    "menu.boop": "Boop nose",
    "menu.hide": "Hide me",
    "menu.mood": "mood",
    "menu.plays": "plays",
    "hint.walk": "walk",
    "hint.click": "click me!",
    "hint.mute": "mute",
    "hint.unmute": "unmute",
    "hint.hide": "hide",
    "hint.unhide": "♡ bring Milkoş back",
    "door.runs": "Milkoş Runs",
    "door.fish": "Fish Catch",
    "door.yarn": "Yarn Chase",
    "door.nap": "Nap Timer",
    "door.mice": "Milkoş’s Mice",
    "door.press": "press E to enter",
    "lang.label": "Lang",
  },
  tr: {
    "cat.name": "Milkoş",
    "cat.intro": "merhaba, benim adım Milkoş 🐾",
    "sec.manifesto": "her şeyi iki kez okur.",
    "sec.works": "altı staj. saydım.",
    "sec.pour": "bana da minik bi kahve?",
    "sec.projects": "bunlar hâlâ fırında.",
    "sec.grid": "eşleştirin! ben tribündeyim.",
    "sec.skills": "aynı zamanda kutu açmayı da bilir.",
    "sec.contact": "purr-lütfen ona bir not yaz ♡",
    "menu.title": "Milkoş ile oyna",
    "menu.runs": "Milkoş Koşuyor",
    "menu.fish": "Balık Yakala",
    "menu.yarn": "Yumak Kovala",
    "menu.nap": "Kestirme Zamanı",
    "menu.mice": "Milkoş’un Fareleri",
    "menu.boop": "Burnuna dokun",
    "menu.hide": "Beni gizle",
    "menu.mood": "ruh hâli",
    "menu.plays": "oyun",
    "hint.walk": "yürü",
    "hint.click": "tıkla!",
    "hint.mute": "sustur",
    "hint.unmute": "aç",
    "hint.hide": "gizle",
    "hint.unhide": "♡ Milkoş’u geri getir",
    "door.runs": "Milkoş Koşuyor",
    "door.fish": "Balık Yakala",
    "door.yarn": "Yumak Kovala",
    "door.nap": "Kestirme Zamanı",
    "door.mice": "Milkoş’un Fareleri",
    "door.press": "girmek için E’ye bas",
    "lang.label": "Dil",
  },
  es: {
    "cat.name": "Milkoş",
    "cat.intro": "hola, me llamo Milkoş 🐾",
    "sec.manifesto": "lo lee todo dos veces.",
    "sec.works": "seis prácticas. las conté.",
    "sec.pour": "¿me haces un cafecito?",
    "sec.projects": "estos aún se están cocinando.",
    "sec.grid": "¡emparéjalos! te apoyo.",
    "sec.skills": "también sabe abrir latas.",
    "sec.contact": "purr-favor, escríbele una nota ♡",
    "menu.title": "Juega con Milkoş",
    "menu.runs": "Milkoş Corre",
    "menu.fish": "Atrapa Peces",
    "menu.yarn": "Persigue el Ovillo",
    "menu.nap": "Tiempo de Siesta",
    "menu.mice": "Los Ratones de Milkoş",
    "menu.boop": "Toca la nariz",
    "menu.hide": "Ocúltame",
    "menu.mood": "ánimo",
    "menu.plays": "partidas",
    "hint.walk": "caminar",
    "hint.click": "¡tócame!",
    "hint.mute": "silenciar",
    "hint.unmute": "sonido",
    "hint.hide": "ocultar",
    "hint.unhide": "♡ trae a Milkoş de vuelta",
    "door.runs": "Milkoş Corre",
    "door.fish": "Atrapa Peces",
    "door.yarn": "Persigue el Ovillo",
    "door.nap": "Tiempo de Siesta",
    "door.mice": "Los Ratones de Milkoş",
    "door.press": "pulsa E para entrar",
    "lang.label": "Idioma",
  },
};

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      // Purge any legacy localStorage preference so a fresh tab always
      // starts in Turkish. Only sessionStorage persists across refreshes.
      localStorage.removeItem("milkos_lang");
      const saved = sessionStorage.getItem("milkos_lang");
      if (saved && DICT[saved]) return saved;
    } catch (e) {
      /* noop */
    }
    return "tr";
  });

  const setLang = (l) => {
    if (!DICT[l]) return;
    setLangState(l);
    try {
      sessionStorage.setItem("milkos_lang", l);
    } catch (e) {
      /* noop */
    }
    document.documentElement.setAttribute("lang", l);
  };

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const t = (key) => DICT[lang][key] || DICT.en[key] || key;

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

export function LangSwitcher({ className = "" }) {
  const { lang, setLang, t } = useI18n();
  return (
    <div
      className={`fixed top-4 right-4 md:top-3.5 md:right-4 z-[90] flex items-center gap-1 bg-[color:var(--paper)] rounded-full border-2 border-[color:var(--ink)] px-1.5 py-1 shadow-[2px_2px_0_0_var(--ink)] font-mono-label text-[10px] uppercase tracking-widest ${className}`}
      data-testid="lang-switcher"
    >
      <span className="opacity-60 px-2">{t("lang.label")}</span>
      {["tr", "en", "es"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          aria-pressed={lang === l}
          className={`px-2 py-1 rounded-full transition-colors ${
            lang === l
              ? "bg-[color:var(--ink)] text-[color:var(--paper)]"
              : "hover:bg-[color:var(--pink)]"
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
